import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import classNames from 'classnames/bind'
import { Swiper, SwiperSlide } from 'swiper/react'
import { isEmpty, isNull, isUndefined } from 'lodash-es'
import { useTranslation } from 'next-i18next'

// Libs
import handleRoutePushWithQuery from '@/methods/url/handleRoutePushWithQuery'
import useMedia from '@/hooks/useMedia'
import useSearchData from '@/hooks/useSearchData'
import useSearchHotKey from '@/hooks/useSearchHotKey'
import { handleGtmClickHost, handleGtmClickMerchant } from '@/methods/handleGtmEvent'

// Components
import ColorfulTab from '@/components/shared/ColorfulTab'
import MallList from '@/components/shared/MallList'
import Icon from '@/assets/icons'
import LinkCard from './components/LinkCard'
import SearchInput from '@/components/shared/SearchInput'
import Empty from '@/components/shared/Empty'
import HotSearchKeywordList from '@/components/shared/HotSearchKeywordList'
import LiveList from '@/components/shared/LiveList'
import Loading from '@/components/common/Loading'

// Constants
import { ROUTE_PATH } from '@/constants/routePath'
import { SEARCH_PRODUCT_FILTER_TAB_DATA, SEARCH_TYPE, searchTabsData } from '@/constants/search'
import { GTM_LOCATION, GtmItemListName } from '@/constants/gtmEvent'

// Styles
import styles from './style.module.scss'

const cx = classNames.bind(styles)
const SEARCH_SWIPER_COUNT = 5
const SEARCH_MOBILE_SWIPER_COUNT = 1.5
const SEARCH_TABLET_SWIPER_COUNT = 3

const SearchComponent = () => {
  const router = useRouter()
  const isTag = router.pathname === ROUTE_PATH.SEARCH_TAG
  const [searchTabActiveIndex, setSearchTabActiveIndex] = useState(
    router.query.type ? searchTabsData.findIndex((item) => item.value === router.query.type) : 0,
  )
  const [keyword, setKeyword] = useState(router.query.keyword || '')
  const [currentProductSort, setCurrentProductSort] = useState(SEARCH_PRODUCT_FILTER_TAB_DATA[0])

  const { hotKeyData } = useSearchHotKey({})

  const { isDesktop, isTablet } = useMedia()
  const swiperCount = isDesktop ? SEARCH_SWIPER_COUNT : isTablet ? SEARCH_TABLET_SWIPER_COUNT : SEARCH_MOBILE_SWIPER_COUNT

  const { searchData, isSearchDataFetched, isSearchDataSuccess, productPaginationData } = useSearchData({
    keyword: keyword as string,
    productSort: currentProductSort.sort,
    isProductPagination: true,
    isTag: isTag,
  })
  const searchHotKeyData =
    !isUndefined(hotKeyData?.keywords) && !isNull(hotKeyData.keywords) ? hotKeyData?.keywords.map((item: any) => item.keyword) : []
  const { t } = useTranslation()

  const liveListData = {
    data: {
      pages: [{ roomList: searchData?.room }],
    },
    isFetchingNextPage: false,
    fetchNextPage: () => {},
    isFetched: isSearchDataFetched,
    isSuccess: isSearchDataSuccess,
  }

  const routerInitFunc = () => {
    if (router.isReady) {
      if (router.query.keyword) {
        setKeyword(router.query.keyword as string)
      }
      if (router.query.type) {
        setSearchTabActiveIndex(searchTabsData.findIndex((item) => item.value === router.query.type))
      }
    }
  }
  useEffect(() => {
    routerInitFunc()
  }, [router.isReady, router.query])

  const displayEmpty = [...searchData.room, ...searchData.roomKol, ...searchData.merchant, ...searchData.product].length === 0 || isEmpty(keyword)

  const handleProductSeeMoreClick = () => {
    const activeIndex = searchTabsData.findIndex((item) => item.value === SEARCH_TYPE.PRODUCT)
    handleRoutePushWithQuery({ router, pathname: ROUTE_PATH.SEARCH, query: { keyword, type: SEARCH_TYPE.PRODUCT } })
    setSearchTabActiveIndex(activeIndex)
  }
  return (
    <div className={cx('search')}>
      <div className={cx('container')}>
        <div className={cx('search__content')}>
          <SearchInput />
          {(!isSearchDataFetched || !isSearchDataSuccess) && <Loading isFull={false} />}
          {isSearchDataSuccess && displayEmpty ? (
            <>
              <div className={cx('search__content-result')}>
                <div className={cx('search__content-result-heading')}>
                  <div className={cx('search__content-result-text')}>{t('Search_Result_Text', { keyword })}</div>
                </div>
              </div>
              <div className={cx('search__content-result__content')}>
                <Empty title={t('Search_Result_Empty_Title')} />
                <HotSearchKeywordList displayAmount={8} data={searchHotKeyData} />
              </div>
            </>
          ) : (
            <>
              {isSearchDataFetched && (
                <div className={cx('search__content-result')}>
                  <div className={cx('search__content-result-heading')}>
                    <div className={cx('search__content-result-text')}>{t('Search_Result_Text', { keyword })}</div>

                    <ColorfulTab
                      activeIndex={searchTabActiveIndex}
                      onChange={(value) => {
                        const activeIndex = searchTabsData.findIndex((item) => item.value === value)
                        handleRoutePushWithQuery({
                          router,
                          pathname: isTag ? ROUTE_PATH.SEARCH_TAG : ROUTE_PATH.SEARCH,
                          query: { keyword, type: value },
                        })
                        setSearchTabActiveIndex(activeIndex)
                      }}
                      values={searchTabsData}
                    />
                  </div>

                  <>
                    {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.PRODUCT && (
                      <div className={cx('search__content-result-product-filter')}>
                        {SEARCH_PRODUCT_FILTER_TAB_DATA.map((tabData) => (
                          <div
                            key={tabData.label}
                            className={cx('search__content-result-product-filter__tab', {
                              active: tabData === currentProductSort,
                            })}
                            onClick={() => {
                              setCurrentProductSort(tabData)
                            }}
                          >
                            {t(tabData.label)}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* 直播間搜尋結果 */}
                    <div className={cx('search__content-result__content')}>
                      {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.LIVE &&
                        searchData?.roomKol?.length < 1 &&
                        searchData?.room?.length < 1 && <Empty title={t('Search_Result_Empty_Title')} />}
                      {(searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.LIVE ||
                        searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.ALL) &&
                        searchData?.roomKol?.length > 0 && (
                          <div className={cx('search__content-result__content__row')}>
                            <div className={cx('search__content-result__content__row-header')}>
                              {<p>{t('Common_Host')}</p>}
                              {searchData?.roomKol?.length > swiperCount && (
                                <div
                                  onClick={() => {
                                    handleRoutePushWithQuery({ router, pathname: ROUTE_PATH.SEARCH_LIVE, query: { keyword, type: SEARCH_TYPE.LIVE } })
                                  }}
                                  className={cx('search__content-result__content__row-header-more')}
                                >
                                  <div>{t('Search_Result_See_More')}</div>
                                  <div className={cx('search__content-result__content__row-header-more-icon')}>
                                    <Icon.ChevronRight />
                                  </div>
                                </div>
                              )}
                            </div>
                            <Swiper
                              className={cx('search__content-result__content__row-cards')}
                              slidesPerView={swiperCount}
                              spaceBetween={10}
                              allowTouchMove={true}
                              grabCursor
                            >
                              {searchData?.roomKol?.map((data) => (
                                <SwiperSlide key={data.memberInfoId}>
                                  <LinkCard
                                    title={data.nickName}
                                    image={data.headshotUrl}
                                    handleClick={() => {
                                      handleRoutePushWithQuery({ router, pathname: `${ROUTE_PATH.USER}/${data.memberInfoId}`, query: { type: 0 } })
                                      handleGtmClickHost({
                                        location: GTM_LOCATION.SEARCH_RESULT,
                                        hostId: data.memberInfoId,
                                      })
                                    }}
                                  />
                                </SwiperSlide>
                              ))}
                              <div className={cx('search__content-result__content__row-cards-mask')} />
                            </Swiper>
                            {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.LIVE && (
                              <LiveList liveListData={liveListData} gtmLocation={GTM_LOCATION.SEARCH_RESULT} />
                            )}
                          </div>
                        )}
                      {/* 賣家搜尋結果 */}
                      {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.ALL && searchData?.merchant?.length > 0 && (
                        <div className={cx('search__content-result__content__row')}>
                          <div className={cx('search__content-result__content__row-header')}>
                            <p>{t('Search_Result_Header_Merchant')}</p>
                            {searchData?.merchant?.length > swiperCount && (
                              <div
                                onClick={() => {
                                  handleRoutePushWithQuery({
                                    router,
                                    pathname: ROUTE_PATH.SEARCH_MERCHANT,
                                    query: { keyword },
                                  })
                                }}
                                className={cx('search__content-result__content__row-header-more')}
                              >
                                <div>{t('Search_Result_See_More')}</div>
                                <div className={cx('search__content-result__content__row-header-more-icon')}>
                                  <Icon.ChevronRight />
                                </div>
                              </div>
                            )}
                          </div>
                          <Swiper
                            slidesPerView={swiperCount}
                            className={cx('search__content-result__content__row-cards')}
                            spaceBetween={10}
                            allowTouchMove={true}
                            grabCursor
                          >
                            {searchData?.merchant?.map((data) => (
                              <SwiperSlide key={data.merchantId}>
                                <LinkCard
                                  title={data.storeName}
                                  description={data.intro}
                                  image={data.headshotUrl}
                                  handleClick={() => {
                                    handleRoutePushWithQuery({ router, pathname: `${ROUTE_PATH.USER}/${data.memberInfoId}`, query: { type: 2 } })
                                    handleGtmClickMerchant({
                                      location: GTM_LOCATION.SEARCH_RESULT,
                                      merchantDetail: {
                                        merchantId: data.memberInfoId,
                                        merchantName: data.storeName,
                                      },
                                    })
                                  }}
                                />
                              </SwiperSlide>
                            ))}
                            <div className={cx('search__content-result__content__row-cards-mask')} />
                          </Swiper>
                        </div>
                      )}
                      {/* 商品搜尋結果 */}
                      {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.PRODUCT && searchData?.product?.length === 0 && (
                        <Empty title={t('Search_Result_Empty_Title')} />
                      )}

                      {(searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.PRODUCT ||
                        searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.ALL) &&
                        searchData?.product?.length > 0 && (
                          <div className={cx('search__content-result__content__row')}>
                            {searchTabsData[searchTabActiveIndex].value === SEARCH_TYPE.ALL && (
                              <div className={cx('search__content-result__content__row-header')}>
                                <p>{t('Search_Result_Header_Product')}</p>
                                <div className={cx('search__content-result__content__row-header-more')} onClick={handleProductSeeMoreClick}>
                                  <div>{t('Search_Result_See_More')}</div>
                                  <div className={cx('search__content-result__content__row-header-more-icon')}>
                                    <Icon.ChevronRight />
                                  </div>
                                </div>
                              </div>
                            )}
                            <MallList
                              categoryListData={productPaginationData}
                              gtmItemEventParams={{
                                itemListName: `${GtmItemListName.SEARCH_RESULT}`,
                              }}
                            />
                          </div>
                        )}
                    </div>
                  </>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
export default SearchComponent
